import { eventChannel } from 'redux-saga';
import { all, call, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { Action } from 'redux-actions';
import {
  BillingInfoPayload,
  HostedFields,
  HostedFieldsStateObject,
  HostedFieldsTokenizePayload,
  ThreeDSecure,
} from 'braintree-web';
import { HostedFieldEventType } from 'braintree-web/hosted-fields';

import { keysToCamelCase } from '@adjust/dashboard-core/lib/fp';
import errorHandler from '@adjust/dashboard-core/lib/errorHandler/errorHandler';
import {
  createInstances,
  getSupportedCardTypes,
  teardown,
  verifyCard,
  normalizeValidationFields,
  getCreditCardType,
  PaymentTheme,
  getBraintreeErrorMessage,
} from '@lib/braintree/braintree';
import { getDefaultPaymentMethod, getClientToken, savePaymentMethod } from '@api/payment-methods';
import {
  updateCardFailure,
  updateCardSuccess,
  changeCardType,
  createInstanceSuccess,
  createInstance,
  updateCardCancel,
  updateCard,
  changeCardValidity,
  fetchPaymentMethod,
  fetchPaymentMethodSucceed,
  fetchPaymentMethodFailed,
} from '@redux/ducks/paymentMethods';

// Channels

export function hostedFieldsChannel(hostedFieldsInstance: HostedFields, eventName: HostedFieldEventType) {
  return eventChannel(emit => {
    const validityChangeHandler = (event: HostedFieldsStateObject) => emit(event);
    hostedFieldsInstance.on(eventName, validityChangeHandler);

    return () => hostedFieldsInstance.off(eventName, validityChangeHandler);
  });
}

// Workers

export function* saveCardTask(
  hostedFieldsInstance: HostedFields,
  threeDSecureInstance: ThreeDSecure,
  { payload }: Action<BillingInfoPayload>,
) {
  try {
    const tokenizeFn: () => Promise<HostedFieldsTokenizePayload> = hostedFieldsInstance.tokenize;
    const tokenizeResult: HostedFieldsTokenizePayload = yield call([hostedFieldsInstance, tokenizeFn]);
    const nonce = yield call(verifyCard, threeDSecureInstance, {
      ...tokenizeResult,
      billingInfo: payload,
    });
    const updatedPaymentMethod = yield call(savePaymentMethod, nonce);

    yield put(updateCardSuccess(keysToCamelCase(updatedPaymentMethod)));
  } catch (err) {
    if (err.details) {
      err.message = getBraintreeErrorMessage(err);
    }

    errorHandler.trackError(err);
    yield put(updateCardFailure(err));
  }
}

export function* validityChangeTask({ fields }) {
  const validationFields = normalizeValidationFields(fields);

  yield put(changeCardValidity(validationFields));
}

export function* cardTypeChangeTask({ cards }) {
  const cardType = getCreditCardType(cards);

  yield put(changeCardType(cardType));
}

export function* fetchPaymentMethodTask() {
  try {
    const paymentMethod = yield call(getDefaultPaymentMethod);

    yield put(fetchPaymentMethodSucceed(keysToCamelCase(paymentMethod)));
  } catch (error) {
    yield put(fetchPaymentMethodFailed());
  }
}

export function* createInstanceTask({ payload }: Action<PaymentTheme>) {
  try {
    const token = yield call(getClientToken);
    const [threeDSecureInstance, hostedFieldsInstance] = yield call(createInstances, token, payload);
    const supportedCardTypes = yield call(getSupportedCardTypes, threeDSecureInstance);
    const validityChangeChannel = yield call(hostedFieldsChannel, hostedFieldsInstance, 'validityChange');
    const cardTypeChangeChannel = yield call(hostedFieldsChannel, hostedFieldsInstance, 'cardTypeChange');

    yield put(createInstanceSuccess(supportedCardTypes));

    yield takeEvery(updateCard, saveCardTask, hostedFieldsInstance, threeDSecureInstance);
    yield takeEvery(validityChangeChannel, validityChangeTask);
    yield takeEvery(cardTypeChangeChannel, cardTypeChangeTask);

    yield take(updateCardCancel);
    yield call(teardown, hostedFieldsInstance);
  } catch (err) {
    yield put(updateCardFailure(err));
  }
}

// Watchers

export function* watchInstanceCreation() {
  yield takeLatest(createInstance, createInstanceTask);
}

export function* watchFetchPaymentMethod() {
  yield takeLatest(fetchPaymentMethod, fetchPaymentMethodTask);
}

// Saga

export default function* paymentMethodsSaga() {
  yield all([watchInstanceCreation(), watchFetchPaymentMethod()]);
}
